// src/components/MapSidePanel.js
// Panneau latéral desktop (style Airbnb) de la carte. Deux vues :
//  - LISTE : sites avec plongées à venir (survol → highlight, clic → fiche).
//  - FICHE : détail d'un site (conditions, planifier, plongées + actions).
// Visible uniquement >= 1024px (géré par le CSS). Le mobile garde la carte plein écran.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import SiteConditions from "./SiteConditions";
import UserLink from "./UserLink";
import { diveVisibilityMeta } from "../utils/visibility";
import "./MapSidePanel.css";

const daysSince = (dateStr) => {
    if (!dateStr) return Infinity;
    const d = new Date(dateStr + "T00:00:00");
    const now = new Date();
    return Math.round((new Date(now.getFullYear(), now.getMonth(), now.getDate()) - d) / 86400000);
};

export default function MapSidePanel({
    sites, activeSite, currentUser, latestConditions, invitations = [], onAccept, onDecline,
    onHover, onSelect, onBack, onCollapse, onPlan, onJoin, onUnsubscribe, onChat, onParticipants, onEdit,
}) {
    const { t, i18n } = useTranslation();
    const [filter, setFilter] = useState("all"); // "all" | "dives" | "invitations"

    // Si on est sur "invitations" mais qu'il n'y en a plus (accepté/refusé), on retombe sur "all".
    const activeFilter = filter === "invitations" && invitations.length === 0 ? "all" : filter;
    const fmtTime = (t2) => (t2 ? t2.slice(0, 5) : "");

    const CollapseBtn = () => (
        <button type="button" className="msp-collapse" onClick={onCollapse} aria-label={t("mapPanel.collapse")} title={t("mapPanel.collapse")}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="15 18 9 12 15 6" />
            </svg>
        </button>
    );

    const fmtDate = (d) => {
        if (!d) return "";
        try {
            return new Date(d + "T00:00:00").toLocaleDateString(i18n.language, { day: "numeric", month: "short" });
        } catch { return d; }
    };

    // ---------- Vue FICHE ----------
    if (activeSite) {
        const site = activeSite;
        const dives = Array.isArray(site.plannedDives) ? site.plannedDives : [];
        return (
            <aside className="map-side-panel">
                <CollapseBtn />
                <div className="msp-head msp-head--fiche">
                    <button type="button" className="msp-back" onClick={onBack}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                        {t("mapPanel.back")}
                    </button>
                    <h2 className="msp-title">{site.name}</h2>
                    {site.canton && <span className="msp-canton">{site.canton}</span>}
                </div>

                <div className="msp-fiche-body">
                    <button type="button" className="msp-plan" onClick={() => onPlan(site)}>
                        {t("map.planDive")}
                    </button>

                    {dives.length > 0 && (
                        <>
                            <h3 className="msp-section">{t("map.plannedDives")}</h3>
                            <ul className="msp-dives">
                                {dives.map((dive) => {
                                    const isOwner = currentUser?.id === dive.user_id;
                                    return (
                                        <li key={dive.id} className="msp-dive">
                                            <div className="msp-dive-top">
                                                <span className="msp-dive-who">
                                                    <UserLink id={dive.user?.id || dive.user_id} firstName={dive.user?.first_name} lastName={dive.user?.last_name} fallback={t("map.firstName")} />
                                                </span>
                                                <span className="msp-dive-date">
                                                    {fmtDate(dive.planned_date)}{dive.planned_time ? ` · ${dive.planned_time}` : ""}
                                                </span>
                                            </div>
                                            {(() => {
                                                const v = diveVisibilityMeta(dive.visibility);
                                                return (
                                                    <div style={{ fontSize: 12, color: "var(--muted, #777)", display: "flex", alignItems: "center", gap: 6, margin: "1px 0 2px" }}>
                                                        <span style={{ width: 7, height: 7, borderRadius: "50%", background: v.color, flexShrink: 0 }} />
                                                        {t(`map.diveCard.visibilityLabel.${v.key}`)}
                                                    </div>
                                                );
                                            })()}
                                            <div className="msp-dive-meta">
                                                {dive.dive_type ? t(`diveTypes.${dive.dive_type}`) : "—"}
                                                {" · "}{dive.gas ? t(`gasTypes.${dive.gas}`) : "—"}
                                                {" · "}{dive.config ? t(`configTypes.${dive.config}`) : "—"}
                                            </div>
                                            {dive.notes && <div className="msp-dive-notes">{dive.notes}</div>}

                                            <div className="msp-dive-actions">
                                                {isOwner ? (
                                                    <button className="msp-btn msp-btn--ghost" onClick={() => onEdit(dive, site)}>{t("map.editDive")}</button>
                                                ) : dive.joined ? (
                                                    <>
                                                        <button className="msp-btn" onClick={() => onChat(dive)}>{t("map.buttons.chat")}</button>
                                                        <button className="msp-btn msp-btn--warn" onClick={() => onUnsubscribe(dive.id)}>{t("map.buttons.unsubscribe")}</button>
                                                    </>
                                                ) : (
                                                    <button className="msp-btn" onClick={() => onJoin(dive.id)}>{t("map.buttons.join")}</button>
                                                )}
                                                <button className="msp-btn msp-btn--ghost" onClick={() => onParticipants(dive)}>{t("chat.participants")}</button>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    )}

                    <div className="msp-conditions">
                        <SiteConditions apiUrl={process.env.REACT_APP_API_URL} siteId={site.id} />
                    </div>

                    <h3 className="msp-section">{t("mapPanel.about")}</h3>
                    {site.description ? (
                        <p className="msp-fiche-desc">{site.description}</p>
                    ) : (
                        <p className="msp-fiche-desc msp-fiche-desc--empty">{t("map.siteDetails.noDescription")}</p>
                    )}
                </div>
            </aside>
        );
    }

    // ---------- Vue LISTE (tous les sites, ceux avec plongées en tête) ----------
    const rows = (Array.isArray(sites) ? sites : [])
        .filter((site) => site.coordinates) // besoin de coordonnées pour voler dessus
        .map((site) => {
            const dives = Array.isArray(site.plannedDives) ? site.plannedDives : [];
            const dates = dives.map((d) => d.planned_date).filter(Boolean).sort();
            const nextDate = dates[0] || null;
            const userJoined = dives.some((d) => d.joined || d.user_id === currentUser?.id);
            return { site, count: dives.length, nextDate, userJoined, hasPlannedDive: site.hasPlannedDive };
        })
        .sort((a, b) => {
            // Sites avec plongées d'abord (triés par prochaine date), puis les autres (alpha).
            if (a.count && !b.count) return -1;
            if (!a.count && b.count) return 1;
            if (a.count && b.count) return (a.nextDate || "9999").localeCompare(b.nextDate || "9999");
            return (a.site.name || "").localeCompare(b.site.name || "");
        });

    const dotColor = (r) => (r.userJoined ? "#2e9e5b" : r.hasPlannedDive ? "var(--primary-blue)" : "#e05252");

    // Filtre d'affichage : tous les sites, ou seulement ceux avec des plongées à venir.
    const visibleRows = activeFilter === "dives" ? rows.filter((r) => r.count > 0) : rows;

    return (
        <aside className="map-side-panel" aria-label={t("mapPanel.title")}>
            <CollapseBtn />
            <div className="msp-head">
                <h2 className="msp-title">{t("mapPanel.title")}</h2>
                <div className="msp-filter" role="tablist" aria-label={t("mapPanel.title")}>
                    <button type="button" className={activeFilter === "all" ? "active" : ""} onClick={() => setFilter("all")}>
                        {t("mapPanel.filterAll")}
                    </button>
                    <button type="button" className={activeFilter === "dives" ? "active" : ""} onClick={() => setFilter("dives")}>
                        {t("mapPanel.filterDives")}
                    </button>
                    {invitations.length > 0 && (
                        <button type="button" className={`msp-filter-inv${activeFilter === "invitations" ? " active" : ""}`} onClick={() => setFilter("invitations")}>
                            {t("mapPanel.filterInvitations")}
                            <span className="msp-inv-badge">{invitations.length}</span>
                        </button>
                    )}
                </div>
            </div>

            {activeFilter === "invitations" ? (
                <ul className="msp-inv-list">
                    {invitations.map((inv) => (
                        <li key={inv.id} className="msp-inv-card">
                            <div className="msp-inv-info">
                                <div className="msp-inv-site">{inv.site_name || "?"}</div>
                                <div className="msp-inv-when">
                                    {fmtDate(inv.planned_date)}{inv.planned_time ? ` · ${fmtTime(inv.planned_time)}` : ""}
                                </div>
                                <div className="msp-inv-by">
                                    {t("invitations.by")}{" "}
                                    <UserLink id={inv.user_id || inv.user?.id} firstName={inv.user?.first_name} lastName={inv.user?.last_name} fallback="?" />
                                </div>
                            </div>
                            <div className="msp-inv-actions">
                                <button type="button" className="msp-inv-accept" onClick={() => onAccept && onAccept(inv)}>
                                    {t("invitations.acceptShort")}
                                </button>
                                <button type="button" className="msp-inv-decline" onClick={() => onDecline && onDecline(inv)} title={t("invitations.decline")} aria-label={t("invitations.decline")}>
                                    ✕
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            ) : visibleRows.length === 0 ? (
                <div className="msp-empty">
                    <p>{filter === "dives" ? t("mapPanel.empty") : t("mapPanel.emptyNoSites")}</p>
                    <p className="msp-empty-hint">{filter === "dives" ? t("mapPanel.emptyHint") : ""}</p>
                </div>
            ) : (
                <ul className="msp-list">
                    {visibleRows.map((r) => {
                        const lc = latestConditions?.[r.site.id];
                        const fresh = lc && daysSince(lc.observed_date) <= 14;
                        return (
                            <li key={r.site.id}>
                                <button
                                    type="button"
                                    className="msp-row"
                                    onMouseEnter={() => onHover(r.site.id)}
                                    onMouseLeave={() => onHover(null)}
                                    onClick={() => onSelect(r.site)}
                                >
                                    <span className="msp-dot" style={{ background: dotColor(r) }} />
                                    <span className="msp-row-main">
                                        <span className="msp-row-top">
                                            <span className="msp-name">{r.site.name}</span>
                                            {r.nextDate && <span className="msp-date">{fmtDate(r.nextDate)}</span>}
                                        </span>
                                        <span className="msp-row-sub">
                                            {r.site.canton && <span className="msp-canton">{r.site.canton}</span>}
                                            {r.count > 0 ? (
                                                <span className="msp-row-dives">{t("map.tooltip.upcomingDives", { count: r.count })}</span>
                                            ) : (
                                                <span className="msp-row-nodive">{t("mapPanel.noDive")}</span>
                                            )}
                                            {lc && (
                                                <span className="msp-cond" title={t("conditions.latestTitle")}>
                                                    <span className="msp-cond-dot" style={{ background: fresh ? "#2e9e5b" : "#b9c3cc" }} />
                                                </span>
                                            )}
                                        </span>
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </aside>
    );
}
